import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.boundChangeHandler = this.handleInputChange.bind(this);

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('change', this.boundChangeHandler);

    this.renderSubscriptionInfo();
    setTimeout(() => this.renderSubscriptionInfo(), 150);
    setTimeout(() => this.renderSubscriptionInfo(), 500);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('change', this.boundChangeHandler);
  }

  updatePrice = (event) => {
    if (event.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;

    const newProductPrice = event.detail?.data?.html?.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );

    if (newProductPrice) {
      const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
      if (newPrice && priceContainer) {
        priceContainer.replaceWith(newPrice);
      }

      const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

      if (!newNote) {
        volumePricingNote?.remove();
      } else if (!volumePricingNote) {
        newPrice?.insertAdjacentElement('afterend', newNote.cloneNode(true));
      } else {
        volumePricingNote.replaceWith(newNote);
      }
    }

    this.renderSubscriptionInfo();
  };

  handleInputChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (
      target.matches('input[name="selling_plan"]') ||
      target.matches('select[name="selling_plan"]') ||
      target.matches('input[name="id"]') ||
      target.matches('select[name="id"]')
    ) {
      this.renderSubscriptionInfo();
    }
  }

  isProductPage() {
    return (
      document.body.classList.contains('template-product') ||
      window.location.pathname.includes('/products/')
    );
  }

  getScope() {
    return this.closest('.shopify-section, dialog') || document;
  }

  getVariantId(scope) {
    const input =
      scope.querySelector('input[name="id"]') ||
      scope.querySelector('select[name="id"]');

    return input ? String(input.value) : '';
  }

  getSellingPlanData() {
    const jsonEl = document.querySelector('#SellingPlanPrices-global');
    if (!jsonEl) return null;

    try {
      return JSON.parse(jsonEl.textContent);
    } catch {
      return null;
    }
  }

  formatMoneySafe(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const fmt =
        window.Shopify.money_format ||
        window.theme?.moneyFormat ||
        '${{amount}}';

      return window.Shopify.formatMoney(cents, fmt);
    }

    return `$${(cents / 100).toFixed(2)}`;
  }

  removeSubscriptionInfo(container) {
    const existing = container.querySelector('.sub-info');
    if (existing) existing.remove();
  }

  showDefaultPrice(container) {
    const defaultPriceEls = container.querySelectorAll(
      '.price, .compare-at-price, [role="group"]'
    );
    defaultPriceEls.forEach((el) => {
      if (!el.classList.contains('sub-info')) {
        el.style.display = '';
      }
    });
  }

  hideDefaultPrice(container) {
    const defaultPriceEls = container.querySelectorAll(
      '.price, .compare-at-price, [role="group"]'
    );
    defaultPriceEls.forEach((el) => {
      if (!el.closest('.sub-info') && !el.classList.contains('sub-info')) {
        el.style.display = 'none';
      }
    });
  }

  renderSubscriptionInfo() {
    if (!this.isProductPage()) return;

    const container = this.querySelector('[ref="priceContainer"]');
    if (!container) return;

    const scope = this.getScope();
    const variantId = this.getVariantId(scope);
    const data = this.getSellingPlanData();

    this.removeSubscriptionInfo(container);
    this.showDefaultPrice(container);

    if (!variantId || !data || !data[variantId]) return;

    const variantData = data[variantId];
    const oneTime = Number(variantData.one_time_price);

    const plans = variantData.plans || {};
    const planList = Object.values(plans);

    if (!planList.length) return;

    const cheapestPlan = planList.reduce((lowest, p) => {
      if (!lowest) return p;
      return Number(p.price) < Number(lowest.price) ? p : lowest;
    }, null);

    if (!cheapestPlan) return;

    const subPrice = Number(cheapestPlan.price);
    const savings = oneTime - subPrice;

    if (!Number.isFinite(subPrice) || !(savings > 0)) return;

    this.hideDefaultPrice(container);

    const el = document.createElement('div');
    el.className = 'sub-info';

    el.innerHTML = `
      <div class="sub-first__line1">
        <span class="sub-first__price">${this.formatMoneySafe(subPrice)}</span>
        <span class="sub-first__pill">on subscription</span>
        <span class="sub-first__save">(Save ${this.formatMoneySafe(savings)})</span>
      </div>
      <div class="sub-first__line3">
        ${this.formatMoneySafe(oneTime)} one-time
      </div>
    `;

    container.appendChild(el);
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}